/**
 * Lyrixa AI branding for printed and exported documents.
 *
 * THE MARK IS NOT DEFINED HERE. Its geometry lives in lib/brandMark.ts and this module
 * computes an SVG *string* from it, because a print window is written with `document.write`
 * and has no React in it — see that file's header for what the mark is, why it is an
 * amplitude envelope, and what it deliberately is not.
 *
 * It used to be lucide's `Music` glyph, hand-copied as path data (`M9 18V5l12-2v13` plus two
 * circles) so that this string and the sidebar's React element would agree. They agreed
 * because someone kept them agreeing; now they cannot disagree, because there is one set of
 * numbers and two renderers of it. That is the whole reason for the split.
 *
 * Inline rather than `<img src>`: a print window has no origin of its own to resolve a
 * relative URL against, and a printer that silently skips a failed image would print an
 * unbranded page.
 *
 * WHAT CHANGES ON PAPER, and why it is not simply the screen design scaled:
 *
 *   The page is white. Printing #101114 across A4 is a page of ink, it is what a
 *   printer's "background graphics off" default discards anyway, and the result of the
 *   discard is the app's light text on white — which is the exact failure the brief
 *   forbids. So print documents are dark-on-white by construction rather than by luck.
 *
 *   The amber stays, because it is the brand, and it is only ever used as a fill behind
 *   the dark glyph or as a hairline rule — never as text on white, where #F2A93C
 *   measures 1.9:1 and would be unreadable. The wordmark is near-black.
 *
 * The certificate keeps its own gold serif treatment (lib/license.ts); it is a
 * different document with a different job, and this module supplies its badge only.
 */
import {
  BRAND_AMBER,
  BRAND_NAME,
} from './brandMark';
import { escapeHtml } from './escapeHtml';

/** The brand amber, as in the token object and the stylesheet. */
const BRAND = BRAND_AMBER;

/** Body ink. Near-black rather than pure, which reads softer at small sizes. */
const INK = '#16171A';

/** Secondary ink for labels and footers. 7.0:1 on white. */
const INK_SOFT = '#55585F';

/** Hairlines. */
const RULE = '#D8D9DD';

const GOLD_LOGO_SRC = '/favicon.png';

/**
 * The Lyrixa AI mark as a standalone inline SVG, `size` pixels square.
 *
 * The rounded square, the amber fill and the four envelope strokes, all in one element so it
 * can be dropped into any document — a print window, a certificate, an exported HTML file —
 * without a stylesheet.
 *
 * DRAWN IN THE 24-UNIT VIEWBOX AND SCALED BY THE VIEWPORT, rather than by recomputing the
 * coordinates at `size`. The previous version scaled a stroked glyph with a `transform` and
 * had to keep a `stroke-width` in step with it or the mark turned into a hairline when
 * enlarged; filled rects in a fixed viewBox have no such failure mode, and it means the
 * emitted numbers are the same ones the React component and the favicon use.
 *
 * `fill` and `glyphColor` exist for one caller: the licence certificate, which is set in gold
 * (#C9A24B) on near-black and has been since the prototype. Dropping the amber mark into it
 * would put two golds a shade apart side by side, which reads as a mistake rather than as a
 * brand. The mark's *shape* is the identity; the palette it sits in belongs to the document.
 * Defaults are the real thing, so every other caller gets it by doing nothing.
 */
export function brandMarkSvg(size = 26, _fill: string = BRAND, _glyphColor: string = INK): string {
  return `<img src="${GOLD_LOGO_SRC}" alt="${BRAND_NAME}" width="${size}" height="${size}" style="display:block;object-fit:contain" />`;
}

/**
 * The mark beside the wordmark — the lockup that heads a printed document.
 *
 * Same arrangement as the sidebar's: mark, 8px gap, "Lyrixa AI" in Space Grotesk at
 * weight 600. The font is named with the same fallback chain the app uses, because a
 * print window has no webfont loaded and no chance to load one; on a machine without
 * Space Grotesk this resolves to the system sans, which is the correct outcome — a
 * document set in the wrong weight is still a branded document, and blocking on a
 * network font would print a blank header.
 */
export function brandLockupHtml(size = 26): string {
  return [
    `<span class="lyx-print-brand">`,
    brandMarkSvg(size),
    `<span class="lyx-print-wordmark">${BRAND_NAME}</span>`,
    `</span>`,
  ].join('');
}

/**
 * The stylesheet every printed document shares.
 *
 * `@page` margins rather than body padding: a printer's own margin is unknown and
 * usually generous, and padding on top of it wastes a band of the page. 18mm is a
 * conventional document margin and leaves room for the running header.
 */
export function printDocumentCss(): string {
  return `
    @page { size: A4; margin: 18mm 16mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      background: #FFFFFF;
      color: ${INK};
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12pt;
      line-height: 1.6;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .lyx-print-head {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 10px;
      border-bottom: 2px solid ${BRAND};
      margin-bottom: 22px;
    }
    .lyx-print-brand { display: flex; align-items: center; gap: 8px; }
    .lyx-print-wordmark {
      font-family: 'Space Grotesk', 'Inter', sans-serif;
      font-weight: 600;
      font-size: 13pt;
      letter-spacing: 0.01em;
      color: ${INK};
    }
    .lyx-print-kind {
      font-size: 8.5pt;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: ${INK_SOFT};
      text-align: right;
    }
    h1 { font-family: 'Space Grotesk', 'Inter', sans-serif; font-size: 20pt; font-weight: 600; margin: 0 0 4px; }
    .lyx-print-meta { font-size: 9.5pt; color: ${INK_SOFT}; margin: 0 0 22px; }
    .lyx-print-foot {
      margin-top: 26px;
      padding-top: 10px;
      border-top: 1px solid ${RULE};
      font-size: 8.5pt;
      color: ${INK_SOFT};
      display: flex;
      justify-content: space-between;
      gap: 16px;
    }
    /* Lyric sheets. A sheet is read line by line, so the break rules matter more than
       the type does: a verse split across a page boundary is the one thing that makes a
       printed sheet unusable to sing from. */
    .lyx-print-lyrics {
      font-family: 'Inter', -apple-system, sans-serif;
      font-size: 11.5pt;
      line-height: 1.75;
      white-space: pre-wrap;
      margin: 0;
      orphans: 3;
      widows: 3;
    }
  `;
}

/** A field on a printed document's detail list. */
export interface PrintField {
  label: string;
  value: string;
}

export interface PrintDocumentOptions {
  /** Browser tab / PDF filename, and the document's <title>. */
  documentTitle: string;
  /** The uppercase kind, top right — "Lyric sheet", "Song details". */
  kind: string;
  /** The <h1>. */
  heading: string;
  /** One line under the heading: genre, length, date. */
  subheading?: string;
  /** Optional label/value list before the body. */
  fields?: PrintField[];
  /** Pre-escaped, pre-marked-up body HTML. See the escaping note below. */
  bodyHtml: string;
  /** Right-hand footer line, e.g. the generation timestamp. */
  footNote?: string;
}

/**
 * Wraps content in the branded page chrome.
 *
 * ESCAPING: every option EXCEPT `bodyHtml` is escaped here, so callers pass plain
 * strings. `bodyHtml` is markup by definition and is therefore the caller's
 * responsibility — the two callers wrap already-escaped text in a single element. That
 * asymmetry is deliberate and it is the one thing to be careful about in this file:
 * song titles and lyrics are user-supplied strings going into an HTML sink, which is
 * harmless while every song is local and is an injection hole the moment songs arrive
 * from a server or a share link.
 */
export function buildPrintDocument(options: PrintDocumentOptions): string {
  const title = escapeHtml(options.documentTitle);
  const kind = escapeHtml(options.kind);
  const heading = escapeHtml(options.heading);
  const subheading = options.subheading ? escapeHtml(options.subheading) : '';
  const footNote = options.footNote ? escapeHtml(options.footNote) : '';

  const fields = (options.fields ?? [])
    .map(
      (f) =>
        `<div class="lyx-print-row"><span>${escapeHtml(f.label)}</span><strong>${escapeHtml(
          f.value
        )}</strong></div>`
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      ${printDocumentCss()}
      .lyx-print-row {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        font-size: 10pt;
        padding: 5px 0;
        border-bottom: 1px solid ${RULE};
      }
      .lyx-print-row span { color: ${INK_SOFT}; }
      .lyx-print-fields { margin: 0 0 20px; }
    </style>
  </head>
  <body>
    <header class="lyx-print-head">
      ${brandLockupHtml()}
      <span class="lyx-print-kind">${kind}</span>
    </header>

    <h1>${heading}</h1>
    ${subheading ? `<p class="lyx-print-meta">${subheading}</p>` : ''}
    ${fields ? `<div class="lyx-print-fields">${fields}</div>` : ''}

    ${options.bodyHtml}

    <footer class="lyx-print-foot">
      <span>Created with Lyrixsa AI · Lyrixa.ai</span>
      ${footNote ? `<span>${footNote}</span>` : ''}
    </footer>
  </body>
</html>`;
}

/**
 * Opens a document in a print window.
 *
 * Returns false when the window could not be opened, which in practice means a popup
 * blocker — the caller toasts. Both print surfaces had this same six lines with the
 * same silent-failure fix applied separately; they now share it, because a third print
 * view forgetting the null check is the failure mode this prevents.
 *
 * `document.write` on a blank window, rather than a Blob URL: the window has no origin
 * of its own, `about:blank` inherits the opener's, and writing to it is the mechanism
 * every browser still supports for exactly this. The document is closed before its
 * images and fonts are awaited. Calling print() immediately after close() races data-URI
 * decoding and can open a print preview with missing artwork.
 */
export function openPrintWindow(html: string): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  void printWhenReady(w);
  return true;
}

function imageReady(image: HTMLImageElement): Promise<void> {
  if (typeof image.decode === 'function') {
    return image.decode().catch(() => undefined);
  }
  if (image.complete) return Promise.resolve();
  return new Promise((resolve) => {
    image.addEventListener('load', () => resolve(), { once: true });
    image.addEventListener('error', () => resolve(), { once: true });
  });
}

function nextFrame(w: Window): Promise<void> {
  if (typeof w.requestAnimationFrame !== 'function') {
    return new Promise((resolve) => w.setTimeout(resolve, 0));
  }
  return new Promise((resolve) => {
    w.requestAnimationFrame(() => w.requestAnimationFrame(() => resolve()));
  });
}

async function printWhenReady(w: Window): Promise<void> {
  const imagesReady = Promise.all(Array.from(w.document.images, imageReady));
  const fontsReady = w.document.fonts?.ready?.catch(() => undefined) ?? Promise.resolve();
  await Promise.all([imagesReady, fontsReady]);
  await nextFrame(w);
  if (w.closed) return;
  w.focus();
  w.print();
}
